import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Lock, Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function ChangePasswordPage() {
  const { user } = useAuth();
  const isParent = user?.role === "parent";

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showC, setShowC] = useState(false);
  const [showN, setShowN] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const tooShort = next.length > 0 && next.length < 8;
  const mismatch = confirm.length > 0 && next !== confirm;
  const valid = current.length > 0 && next.length >= 8 && next === confirm && next !== current;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    try {
      const path = isParent ? "/parent/change-password" : "/auth/change-password";
      await api.post(path, { current_password: current, new_password: next });
      toast.success("Password changed. Please use your new password next time you log in.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="max-w-xl mx-auto">
        <div className="mb-6">
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-foreground tracking-tight">
            Change Password
          </h1>
          <p className="text-sm text-brand-inkMuted mt-1">
            Keep your account secure. Choose a fresh password you don't use anywhere else.
          </p>
        </div>

        <Card className="rounded-3xl p-6 sm:p-8 shadow-floating bg-card">
          <form onSubmit={submit} className="space-y-5" data-testid="change-password-form">
            <div>
              <Label className="font-heading text-foreground">Current password</Label>
              <div className="relative mt-1.5">
                <Input
                  type={showC ? "text" : "password"}
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  placeholder="Enter your current password"
                  className="rounded-2xl h-12 pl-11 pr-11"
                  autoComplete="current-password"
                  required
                  data-testid="cp-current-input"
                />
                <Lock className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-brand-inkMuted" />
                <button type="button" onClick={() => setShowC((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-inkMuted hover:text-foreground"
                  data-testid="cp-current-toggle"
                  aria-label={showC ? "Hide password" : "Show password"}
                >
                  {showC ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="font-heading text-foreground">New password</Label>
              <div className="relative mt-1.5">
                <Input
                  type={showN ? "text" : "password"}
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  placeholder="At least 8 characters"
                  className="rounded-2xl h-12 pl-11 pr-11"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  data-testid="cp-new-input"
                />
                <ShieldCheck className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-brand-inkMuted" />
                <button type="button" onClick={() => setShowN((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-inkMuted hover:text-foreground"
                  data-testid="cp-new-toggle"
                  aria-label={showN ? "Hide password" : "Show password"}
                >
                  {showN ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {tooShort && <p className="text-xs text-[#C62828] mt-1">Must be at least 8 characters.</p>}
            </div>

            <div>
              <Label className="font-heading text-foreground">Confirm new password</Label>
              <Input
                type={showN ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter the new password"
                className="rounded-2xl h-12 mt-1.5"
                autoComplete="new-password"
                required
                data-testid="cp-confirm-input"
              />
              {mismatch && <p className="text-xs text-[#C62828] mt-1">Passwords do not match.</p>}
              {next.length > 0 && next === current && (
                <p className="text-xs text-[#E65100] mt-1">New password must differ from the current one.</p>
              )}
            </div>

            <div className="pt-2 flex justify-end">
              <Button
                type="submit"
                disabled={!valid || submitting}
                className="rounded-full h-12 px-8 bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] hover:from-[#2563EB] hover:to-[#1E40AF] text-white font-heading font-semibold shadow-floating disabled:opacity-50"
                data-testid="cp-submit-btn"
              >
                {submitting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : "Update password"}
              </Button>
            </div>
          </form>
        </Card>

        <p className="text-xs text-brand-inkMuted text-center mt-4">
          Tip: Use a passphrase like <em>"Maple-Llama-Sunrise-42"</em> — easy to remember, hard to guess.
        </p>
      </div>
    </>
  );
}
